import { insightsIntl } from "../strings/intl";
import * as React from "react";
import { useLoaderData } from "react-router";
import { useAnalyticsContext } from "shared-utils";

import { Dimensions, MetricTypes } from "../autogen/metrics";
import { GenericAds } from "../generic-ads-screen";
import { type GenericAdsTabProps } from "../generic-ads-screen/components/generic-ads-tab";
import { type InsightsLoaderReturn } from "../loader";
import { audienceCategoryDataTooltip, audienceDataTooltip } from "../strings/tooltips";
import { getUrls } from "../urls";
import { allHistoricalData } from "../utils/custom-time-range-helpers";
import { dateRangePresets, useDateAndTemp } from "../utils/date";
import { type DashboardOutput, type DataToFetch, getDashboardOutput } from "../utils/fetch-api";
import { addNotes, getNotes } from "../utils/notes";
import { type CustomTimeRange } from "../utils/types";

import {
  getDetailedChartFilters,
  getOverviewChart,
  getOverviewFilters,
  getSpendCpConversionRoasChart,
  getSummaryTable2,
  handleTabClick,
} from "./generic-ads";
import {
  AdPlatform,
  getCampaignPerformanceTableData2,
  getInsightsOverviewChartData2,
  getMetaInsightsSummaryCards2,
  getSpendCpConversionRoasChartData2,
  getSummaryTableData2,
} from "./metrics-generic-ads";

const overviewTooltip = `
#### What is on this tab? \n\n
This page shows your overall Meta ads performance. \n\n
The key three metrics reported on this page are spend, cost per conversion (Spend/conversions) and conversions.
\n\n\n\n
#### When should I use this section? \n\n
We recommend checking this tab daily to monitor the overall performance of your Meta ads account \n\n
\n\n\n\n
#### Who should use this section? \n\n
Anyone who needs to monitor the overall performance of your Meta ads account
`;

const campaignTooltip = `
#### What is on this tab? \n\n
This page shows your meta ads performance breakdown down by campaign.  \n\n
The key metrics reported on this page are Spend, Cost per conversion (Spend/conversions) ,ROAS (Revenue/Spend) , frequency and percent of budget spent on each campaign
\n\n\n\n
#### When should I use this section? \n\n
We recommend checking this tab on weekly basis to monitor the performance of each campaign  \n\n
\n\n\n\n
#### Who should use this section? \n\n
We designed this tab to be used by the individuals running your ad account  \n\n
\n\n\n\n
#### How should I interpret these metrics? \n\n
All metrics are reported using 7 day click or 1 day view attribution window \n\n
`;

const prospectingTooltip = `
#### What is on this tab? \n\n
This page shows your meta ads performance breakdown down by prospecting audiences vs retargeting \n\n
We define prospecting audiences as audiences that haven't interacted with your brand before i.e Broad, interest and lookalike audiences \n\n
We define retargeting audiences as audiences that have interacted with your brand before i.e Onsite engagers, Offsite engagers and imported customer lists. \n\n
The key metrics reported on this page are Spend, Cost per conversion (Spend/conversions) ,ROAS (Revenue/Spend) and percent of budget spent on prospecting vs retargeting over your selected timeframe
\n\n\n\n
#### When should I use this section? \n\n
We recommend checking this tab on weekly basis to monitor performance broken down down by prospecting audiences vs retargeting \n\n
\n\n\n\n
#### Who should use this section? \n\n
We designed this tab to be used by the individuals running your ad account \n\n
\n\n\n\n
#### How should I interpret these metrics? \n\n
All metrics are reported using 7 day click or 1 day view attribution window \n\n
`;

const audienceTooltip = `
#### What is on this tab? \n\n
This page shows your meta ads performance breakdown down by audience e.g Broad, interest, lookalike ,Onsite engagers, Offsite engagers and imported customer lists. \n\n
The key metrics reported on this page are Spend, Cost per conversion (Spend/conversions) ,ROAS (Revenue/Spend) and percent of budget spent on each audience type
\n\n\n\n
#### When should I use this section?\n\n
We recommend checking this tab on weekly basis to monitor the performance of each audience\n\n
\n\n\n\n
#### Who should use this section?\n\n
We designed this tab to be used by the individuals running your ad account\n\n
\n\n\n\n
#### How should I interpret these metrics?\n\n
All metrics are reported using 7 day click or 1 day view attribution window\n\n
`;

const OVERVIEW_METRICS = [
  MetricTypes.META_AD_SPEND,
  MetricTypes.META_AD_COST_PER_CONVERSION,
  MetricTypes.META_AD_ATTRIBUTED_CONVERSION_ROAS,
];

const PERFORMANCE_METRICS = [
  MetricTypes.META_AD_SPEND,
  MetricTypes.META_AD_IMPRESSIONS,
  MetricTypes.META_AD_REACH,
  MetricTypes.META_AD_FREQUENCY,
  MetricTypes.META_AD_CLICKS,
  MetricTypes.META_AD_ATTRIBUTED_CONVERSION_COUNT,
  MetricTypes.META_AD_ATTRIBUTED_CONVERSION_VALUE,
  MetricTypes.META_AD_ATTRIBUTED_CONVERSION_ROAS,
];

/**
 * Entry point
 */
const MetaAds = () => {
  const { customerDomain }: InsightsLoaderReturn = useLoaderData();
  const { dashboardDataPath, notesPath } = getUrls(customerDomain);
  const [isLoading, setIsLoading] = React.useState(false);
  const [requiresDataFetch, setRequiresDataFetch] = React.useState(true);
  const [requiresDataComputation, setRequiresDataComputation] = React.useState(true);
  const [tabDataError, setTabDataError] = React.useState(null);

  const [selectedTabIndex, setSelectedTabIndex] = React.useState(0);

  const [campaignTabDataRaw, setCampaignTabDataRaw] = React.useState(null);
  const [overviewDataCurrentRaw, setOverviewDataCurrentRaw] = React.useState(null);
  const [overviewDataChartRaw, setOverviewDataChartRaw] = React.useState(null);
  const [prospectingTabDataRaw, setProspectingTabDataRaw] = React.useState(null);
  const [prospectingTabDataRawAggregated, setProspectingTabDataRawAggregated] = React.useState(null);
  const [audienceTabDataRaw, setAudienceTabDataRaw] = React.useState(null);
  const [audienceTabDataRawAggregated, setAudienceTabDataRawAggregated] = React.useState(null);

  const [overviewSummaryData, setOverviewSummaryData] = React.useState(null);
  const [overviewChartData, setOverviewChartData] = React.useState(null);
  const [prospectingTabChartData, setProspectingTabChartData] = React.useState(null);
  const [prospectingTabSummaryData, setProspectingTabSummaryData] = React.useState(null);
  const [audienceTabChartData, setAudienceTabChartData] = React.useState(null);
  const [audienceTabSummaryData, setAudienceTabSummaryData] = React.useState(null);
  const [campaignTabData, setCampaignTabData] = React.useState(null);
  const notesValue = React.useRef<string>("");

  const [selectedCurrentTimeRangeEnabled, setSelectedCurrentTimeRangeEnabled] = React.useState(true);
  const [selectedReferenceTimeRangeEnabled, setSelectedReferenceTimeRangeEnabled] = React.useState(true);

  const onChange = () => {
    setRequiresDataFetch(true);
    // eslint-disable-next-line react-hooks/immutability -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    scheduleDataComputation();
  };

  const {
    selectedTimeRange,
    handleTimeRangeChange,
    currentPreset,
    temporalResolution,
    selectedButtonIndex,
    hideWeekly,
    hideMonthly,
    handleTempResChange,
  } = useDateAndTemp(onChange, onChange);

  const isStaff = useAnalyticsContext().isStaff;

  const allData = React.useMemo(
    () => [
      overviewChartData,
      overviewSummaryData,
      prospectingTabChartData,
      prospectingTabSummaryData,
      audienceTabSummaryData,
      audienceTabChartData,
      campaignTabData,
    ],
    [
      audienceTabChartData,
      audienceTabSummaryData,
      campaignTabData,
      overviewChartData,
      overviewSummaryData,
      prospectingTabChartData,
      prospectingTabSummaryData,
    ]
  );

  const allRawData = React.useMemo(
    () => [
      campaignTabDataRaw,
      overviewDataCurrentRaw,
      overviewDataChartRaw,
      prospectingTabDataRaw,
      prospectingTabDataRawAggregated,
      audienceTabDataRaw,
      audienceTabDataRawAggregated,
    ],
    [
      campaignTabDataRaw,
      overviewDataCurrentRaw,
      overviewDataChartRaw,
      prospectingTabDataRaw,
      prospectingTabDataRawAggregated,
      audienceTabDataRaw,
      audienceTabDataRawAggregated,
    ]
  );

  const scheduleDataComputation = React.useCallback(() => {
    setRequiresDataComputation(true);
  }, []);

  const computeDerivedData = () => {
    if (!requiresDataComputation || requiresDataFetch || isLoading) {
      return;
    }

    setCampaignTabData(getCampaignPerformanceTableData2(campaignTabDataRaw, PERFORMANCE_METRICS, AdPlatform.META_ADS));
    setOverviewChartData(
      getInsightsOverviewChartData2(overviewDataChartRaw, selectedTimeRange, temporalResolution, AdPlatform.META_ADS)
    );
    setOverviewSummaryData(getMetaInsightsSummaryCards2(overviewDataCurrentRaw, selectedTimeRange));
    setProspectingTabSummaryData(
      getSummaryTableData2(
        prospectingTabDataRawAggregated,
        OVERVIEW_METRICS,
        Dimensions.AUDIENCE_TYPE,
        AdPlatform.META_ADS
      )
    );
    setProspectingTabChartData(
      getSpendCpConversionRoasChartData2(
        prospectingTabDataRaw,
        selectedTimeRange,
        temporalResolution,
        AdPlatform.META_ADS
      )
    );
    setAudienceTabSummaryData(
      getSummaryTableData2(audienceTabDataRawAggregated, OVERVIEW_METRICS, Dimensions.AUDIENCE, AdPlatform.META_ADS)
    );
    setAudienceTabChartData(
      getSpendCpConversionRoasChartData2(audienceTabDataRaw, selectedTimeRange, temporalResolution, AdPlatform.META_ADS)
    );

    setRequiresDataComputation(false);
  };
  computeDerivedData();

  React.useEffect(() => {
    if (allRawData.every((x) => x)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
      setIsLoading(false);
    }
  }, [allRawData]);

  const fetchData = React.useCallback(
    (
      dataToFetch: DataToFetch[],
      timeRange: CustomTimeRange,
      callback: (res: DashboardOutput) => void,
      aggregateBy?: Dimensions
    ) => {
      getDashboardOutput(
        dashboardDataPath,
        dataToFetch,
        timeRange,
        callback,
        aggregateBy,
        setTabDataError,
        temporalResolution
      );
    },
    [dashboardDataPath, temporalResolution]
  );

  const fetchCampaignData = React.useCallback(() => {
    fetchData(
      [
        {
          metrics: PERFORMANCE_METRICS,
          filename: "meta_insights_campaign_table_ctr",
          groupBys: [Dimensions.CAMPAIGN_ID, Dimensions.CAMPAIGN, Dimensions.CAMPAIGN_OBJECTIVE],
        },
      ],
      selectedTimeRange,
      setCampaignTabDataRaw,
      null
    );
  }, [fetchData, selectedTimeRange]);

  const fetchOverviewDataCurrent = React.useCallback(() => {
    fetchData(
      [
        {
          metrics: OVERVIEW_METRICS,
          filename: "meta_insights_overview_ctr",
          groupBys: [],
        },
      ],
      selectedTimeRange,
      setOverviewDataCurrentRaw,
      null
    );
  }, [fetchData, selectedTimeRange]);

  const fetchOverviewDataChart = React.useCallback(() => {
    fetchData(
      [
        {
          metrics: OVERVIEW_METRICS,
          filename: "meta_insights_overview_ctr",
          groupBys: [],
        },
      ],
      allHistoricalData(selectedTimeRange),
      setOverviewDataChartRaw,
      null
    );
  }, [fetchData, selectedTimeRange]);

  const fetchProspectingDataAggregatedByAudienceType = React.useCallback(() => {
    fetchData(
      [
        {
          metrics: OVERVIEW_METRICS,
          filename: "meta_insights_audience_type_ctr",
          groupBys: [Dimensions.AUDIENCE_TYPE],
        },
      ],
      selectedTimeRange,
      setProspectingTabDataRawAggregated,
      Dimensions.AUDIENCE_TYPE
    );
  }, [fetchData, selectedTimeRange]);

  const fetchProspectingData = React.useCallback(() => {
    fetchData(
      [
        {
          metrics: OVERVIEW_METRICS,
          filename: "meta_insights_audience_type_ctr",
          groupBys: [Dimensions.AUDIENCE_TYPE],
        },
      ],
      selectedTimeRange,
      setProspectingTabDataRaw,
      null
    );
  }, [fetchData, selectedTimeRange]);

  const fetchAudienceData = React.useCallback(() => {
    fetchData(
      [
        {
          metrics: OVERVIEW_METRICS,
          filename: "meta_insights_audience_ctr",
          groupBys: [Dimensions.AUDIENCE],
        },
      ],
      selectedTimeRange,
      setAudienceTabDataRaw,
      null
    );
  }, [fetchData, selectedTimeRange]);

  const fetchAudienceDataAggregatedByAudience = React.useCallback(() => {
    fetchData(
      [
        {
          metrics: OVERVIEW_METRICS,
          filename: "meta_insights_audience_ctr",
          groupBys: [Dimensions.AUDIENCE],
        },
      ],
      selectedTimeRange,
      setAudienceTabDataRawAggregated,
      Dimensions.AUDIENCE
    );
  }, [fetchData, selectedTimeRange]);

  React.useEffect(() => {
    if (requiresDataFetch) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
      setIsLoading(true);

      setCampaignTabDataRaw(null);
      setOverviewDataCurrentRaw(null);
      setOverviewDataChartRaw(null);
      setProspectingTabDataRaw(null);
      setProspectingTabDataRawAggregated(null);
      setAudienceTabDataRaw(null);
      setAudienceTabDataRawAggregated(null);

      fetchCampaignData();
      fetchOverviewDataCurrent();
      fetchOverviewDataChart();
      fetchProspectingData();
      fetchProspectingDataAggregatedByAudienceType();
      fetchAudienceData();
      fetchAudienceDataAggregatedByAudience();

      setRequiresDataFetch(false);
    }
  }, [
    requiresDataFetch,
    fetchCampaignData,
    fetchOverviewDataCurrent,
    fetchOverviewDataChart,
    fetchProspectingData,
    fetchProspectingDataAggregatedByAudienceType,
    fetchAudienceData,
    fetchAudienceDataAggregatedByAudience,
  ]);

  React.useEffect(() => {
    getNotes(notesPath, "meta", (result: any) => {
      notesValue.current = result.content;
    });
  }, [notesPath]);

  let tabData: GenericAdsTabProps[] = [];

  if (allData.every((x) => x)) {
    const overviewFilters = getOverviewFilters(
      isLoading,
      hideWeekly,
      hideMonthly,
      setSelectedCurrentTimeRangeEnabled,
      setSelectedReferenceTimeRangeEnabled,
      selectedButtonIndex,
      selectedCurrentTimeRangeEnabled,
      selectedReferenceTimeRangeEnabled,
      handleTempResChange
    );

    const overviewChart = getOverviewChart(
      overviewChartData,
      selectedCurrentTimeRangeEnabled,
      selectedReferenceTimeRangeEnabled
    );

    const overviewSummary = {
      summaryCards: [
        {
          title: insightsIntl.formatMessage({ id: "Meta Ads Spend", defaultMessage: "Meta Ads Spend" }),
          value: overviewSummaryData.spend,
          tooltip: insightsIntl.formatMessage({
            id: "Meta Ads marketing spend based on Meta platform",
            defaultMessage: "Meta Ads marketing spend based on Meta platform",
          }),
        },
        {
          title: insightsIntl.formatMessage({
            id: "Meta Ads Cost per Conversion",
            defaultMessage: "Meta Ads Cost per Conversion",
          }),
          value: overviewSummaryData.cc,
          tooltip: insightsIntl.formatMessage({
            id: "Meta Ads Spend divided by Meta Ads Conversions",
            defaultMessage: "Meta Ads Spend divided by Meta Ads Conversions",
          }),
        },
        {
          title: insightsIntl.formatMessage({
            id: "Meta Ads Return on Ad Spend",
            defaultMessage: "Meta Ads Return on Ad Spend",
          }),
          value: overviewSummaryData.roas,
          tooltip: insightsIntl.formatMessage({
            id: "Meta Ads attributed sales divided by Meta Ads Spend",
            defaultMessage: "Meta Ads attributed sales divided by Meta Ads Spend",
          }),
        },
      ],
    };

    const audienceFilters = getDetailedChartFilters(hideWeekly, hideMonthly, handleTempResChange, selectedButtonIndex);

    const prospectingTable = getSummaryTable2(prospectingTabSummaryData);
    const prospectingChart = getSpendCpConversionRoasChart(prospectingTabChartData);

    const audienceTable = getSummaryTable2(audienceTabSummaryData);
    const audienceChart = getSpendCpConversionRoasChart(audienceTabChartData);

    tabData = [
      {
        title: insightsIntl.formatMessage({ id: "Account level overview", defaultMessage: "Account level overview" }),
        summaryCards: overviewSummary,
        chartFilters: overviewFilters,
        chartData: overviewChart,
        tooltip: overviewTooltip,
      },
      {
        title: insightsIntl.formatMessage({
          id: "Prospecting vs Retargeting",
          defaultMessage: "Prospecting vs Retargeting",
        }),
        summaryTable: { ...prospectingTable, getToolTipForData: audienceCategoryDataTooltip },
        chartData: prospectingChart,
        chartFilters: {
          ...audienceFilters,
          switchOneName: "prospect-retarget-switch-one",
          switchTwoName: "prospect-retarget-switch-two",
        },
        tooltip: prospectingTooltip,
      },
      {
        title: insightsIntl.formatMessage({ id: "Audience Breakdown", defaultMessage: "Audience Breakdown" }),
        summaryTable: { ...audienceTable, getToolTipForData: audienceDataTooltip },
        chartData: audienceChart,
        chartFilters: {
          ...audienceFilters,
          switchOneName: "audience-breakdown-switch-one",
          switchTwoName: "audience-breakdown-switch-two",
        },
        tooltip: audienceTooltip,
      },
      {
        title: insightsIntl.formatMessage({ id: "Campaign Breakdown", defaultMessage: "Campaign Breakdown" }),
        performanceTableData: {
          performanceBreakdownData: campaignTabData,
        },
        tooltip: campaignTooltip,
      },
    ];
    // eslint-disable-next-line react-hooks/refs -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
    tabData = addNotes(isStaff, notesValue, notesPath, "meta", tabData);
  }

  React.useEffect(() => {
    if (allData.every((x) => x)) {
      // eslint-disable-next-line react-hooks/set-state-in-effect -- Existing violation after eslint-plugin-react-hooks v7 upgrade; remove when fixed.
      setIsLoading(false);
    }
  }, [allData]);

  return (
    <GenericAds
      datePickerHeaderProps={{
        currentPreset,
        startDate: selectedTimeRange.start.format("YYYY-MM-DD"),
        endDate: selectedTimeRange.end.format("YYYY-MM-DD"),
        dateRangePresets: dateRangePresets,
        onChange: (dateRangePreset, startDate, endDate) =>
          handleTimeRangeChange(dateRangePreset as Parameters<typeof handleTimeRangeChange>[0], startDate, endDate),
      }}
      pageTitle="Meta Ads"
      tabDataLoading={requiresDataComputation}
      tabDataError={tabDataError}
      tabData={tabData}
      onSelectTab={handleTabClick(setSelectedTabIndex)}
      selectedTabIndex={selectedTabIndex}
    />
  );
};

export default MetaAds;
